import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Header from '../components/Header';
import Footer from '../components/Footer';

const FindUsername: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    birthYear: '',
    birthMonth: '',
    birthDay: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ maskedUsername: string; createdAt: string } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  // 전화번호 자동 포맷 (하이픈 추가)
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length > 11) value = value.slice(0, 11);

    // 하이픈 자동 삽입
    if (value.length > 7) {
      value = `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7)}`;
    } else if (value.length > 3) {
      value = `${value.slice(0, 3)}-${value.slice(3)}`;
    }

    setFormData(prev => ({ ...prev, phone: value }));
    setError('');
  };

  // 연도 옵션 생성 (1920 ~ 현재)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1920 + 1 }, (_, i) => currentYear - i);

  // 월 옵션
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  // 일 옵션 (선택된 월에 따라)
  const getDaysInMonth = () => {
    const year = parseInt(formData.birthYear) || currentYear;
    const month = parseInt(formData.birthMonth) || 1;
    return new Date(year, month, 0).getDate();
  };
  const dayOptions = Array.from({ length: getDaysInMonth() }, (_, i) => i + 1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setResult(null);

    // 유효성 검사
    if (!formData.name.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }

    const phoneDigits = formData.phone.replace(/[^0-9]/g, '');
    if (phoneDigits.length < 10) {
      setError('전화번호를 정확히 입력해주세요.');
      return;
    }

    if (!formData.birthYear || !formData.birthMonth || !formData.birthDay) {
      setError('생년월일을 선택해주세요.');
      return;
    }

    setLoading(true);

    try {
      // 생년월일 포맷 (YYYY-MM-DD)
      const birthdate = `${formData.birthYear}-${String(formData.birthMonth).padStart(2, '0')}-${String(formData.birthDay).padStart(2, '0')}`;

      const { data, error: funcError } = await supabase.functions.invoke('find-username', {
        body: {
          name: formData.name.trim(),
          phone: phoneDigits,
          birthdate
        }
      });

      if (funcError) {
        console.error('Function error:', funcError);
        setError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      if (!data.success) {
        setError(data.message || '입력하신 정보와 일치하는 계정을 찾을 수 없습니다.');
        return;
      }

      setResult(data.data);
      setSuccess(true);

    } catch (err) {
      console.error('Find username error:', err);
      setError('오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <div className="flex-grow bg-white flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-32 sm:pt-28">
        <div className="max-w-md w-full">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              아이디 찾기
            </h1>
            <p className="text-sm text-gray-600">
              가입 시 등록한 정보를 입력하세요
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded p-8">
            {success && result ? (
              <div className="text-center">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  아이디를 찾았습니다
                </h3>

                <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-2">회원님의 아이디</p>
                  <p className="text-2xl font-bold text-green-600">{result.maskedUsername}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    가입일: {result.createdAt}
                  </p>
                </div>

                <p className="text-xs text-gray-500 mb-6">
                  보안을 위해 아이디 일부가 마스킹(*)되어 표시됩니다.
                </p>

                <div className="space-y-3">
                  <Link
                    to="/login"
                    className="block w-full py-3 px-4 border border-transparent text-base font-medium rounded text-white bg-green-600 hover:bg-green-700 text-center"
                  >
                    로그인하기
                  </Link>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="block w-full py-3 px-4 border border-gray-300 text-base font-medium rounded text-gray-700 bg-white hover:bg-gray-50 text-center"
                  >
                    비밀번호 찾기
                  </button>
                </div>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                {/* 이름 */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    이름
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="appearance-none block w-full px-3 py-2.5 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="이름을 입력하세요"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>

                {/* 전화번호 */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    전화번호
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    className="appearance-none block w-full px-3 py-2.5 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="010-1234-5678"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                  />
                </div>

                {/* 생년월일 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    생년월일
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <select
                      name="birthYear"
                      value={formData.birthYear}
                      onChange={handleInputChange}
                      className="px-3 py-2.5 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    >
                      <option value="">년도</option>
                      {yearOptions.map(year => (
                        <option key={year} value={year}>{year}년</option>
                      ))}
                    </select>
                    <select
                      name="birthMonth"
                      value={formData.birthMonth}
                      onChange={handleInputChange}
                      className="px-3 py-2.5 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    >
                      <option value="">월</option>
                      {monthOptions.map(month => (
                        <option key={month} value={month}>{month}월</option>
                      ))}
                    </select>
                    <select
                      name="birthDay"
                      value={formData.birthDay}
                      onChange={handleInputChange}
                      className="px-3 py-2.5 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    >
                      <option value="">일</option>
                      {dayOptions.map(day => (
                        <option key={day} value={day}>{day}일</option>
                      ))}
                    </select>
                  </div>
                </div>

                {error && (
                  <div className="rounded bg-red-50 border border-red-200 p-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? '조회 중...' : '아이디 찾기'}
                  </button>
                </div>

                <div className="text-center pt-4 border-t border-gray-200 space-y-2">
                  <p className="text-sm text-gray-600">
                    비밀번호를 잊으셨나요?{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-green-600 hover:text-green-700 font-medium hover:underline"
                    >
                      비밀번호 찾기
                    </button>
                  </p>
                  <Link
                    to="/login"
                    className="text-sm text-green-600 hover:text-green-700 font-medium hover:underline block"
                  >
                    로그인 페이지로 돌아가기
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default FindUsername;
